import React, { FC, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { DOORS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/DoorColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import ControllerSearch from "../models/search/ControllerSearch";
import searchColumns from '../models/search/DoorSearch';
import SiteSearch from "../models/search/SiteSearch";
import ZoneSearch from "../models/search/ZoneSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getControllerNames } from "../services/controllerService";
import { deleteDoor, getDoors } from "../services/doorService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getZoneNamesWithPagination } from "../services/zoneService";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { Filter } from "../models/Filter";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    onFilterChange: (filters: Filter[]) => void,
    setCategories: (category: Category[]) => void
}

const DoorsTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.doors);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/hardware/door/';

    useEffect(() => {
        (async () => {
            setLookup([{
                name: LookupTypes.zoneName,
                values: [],
                dataMethod: getZoneNamesWithPagination,
                searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.zoneController,
                values: [],
                dataMethod: getControllerNames,
                searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.zoneSite,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteDoor(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }
    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, DOORS) => void) => {
        setDetails(userLoginId, DOORS);
    }
    // <-- end context

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getDoors}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        lookup={lookup}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="doors"/>
    </TableContextProvider>
};

export default DoorsTab;
